import { useState, useEffect } from 'react';
import { useGetSeries } from '@/api/hooks/useSerie';
import { useGetClasses } from '@/api/hooks/useClasse';
import { useGetDiscplines } from '@/api/hooks/useDiscipline';
import {
  ConfigExamFormValues,
  useConfigExam,
} from '../../../hooks/useConfigExam';
import {
  useCreateExamConfig,
  useGetExamConfigs,
  useUpdateExamConfig,
} from '@/api/hooks/useExamConfig';
import { ExamConfigInput, DisciplineWithLessonsInput } from '@/api/gql/graphql';
import { Options } from '@/components/Common/Selects/Select.types';
import { useToast } from '../../../hooks/useToast';
import { useDisciplineWithLessonStore } from '@/store/ExamStore/DisciplineWithLessonStore';

export const useFormConfigExamLogic = () => {
  const { register, handleSubmit, errors, setValue } = useConfigExam();
  const disciplinesWithLessons = useDisciplineWithLessonStore(
    (state) => state.disciplinesWithLessons,
  );

  // Synchroniser les données du store avec le formulaire
  useEffect(() => {
    if (disciplinesWithLessons.length > 0) {
      setValue('disciplinesWithLessons', disciplinesWithLessons);
    }
  }, [disciplinesWithLessons, setValue]);

  const { data: classes } = useGetClasses({ name: '' });
  const { data: series } = useGetSeries({ classes: null, name: null });
  const { data: disciplines } = useGetDiscplines();
  const { data: examConfigs } = useGetExamConfigs();

  const { mutate: createExamConfig, isPending: isCreating } =
    useCreateExamConfig();
  const { mutate: updateExamConfig, isPending: isUpdating } =
    useUpdateExamConfig();
  const { showToast } = useToast();

  const [isActive, setIsActive] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState<Options[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<Options[]>([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState<Options[]>([]);
  const [examDates, setExamDates] = useState<{
    startDate?: Date;
    endDate?: Date;
    resultDate?: Date;
    giftGivingDate?: Date;
  }>({});

  const syncWithServerData = useDisciplineWithLessonStore(
    (state) => state.syncWithServerData,
  );

  useEffect(() => {
    if (examConfigs && examConfigs.length > 0) {
      const examConfig = examConfigs[0];

      setValue('title', examConfig.title);
      setValue('name', examConfig.name);
      setValue('description', examConfig.description);
      setValue('pointsToEarn', examConfig.pointsToEarn);
      setValue('isEnabled', examConfig.isEnabled);
      setIsActive(examConfig.isEnabled);

      if (examConfig.gifts && examConfig.gifts.length > 0) {
        setValue(
          'gifts',
          examConfig.gifts.map((gift) => ({
            _id: gift._id,
            name: gift.name,
          })),
        );
      }

      const validateAndCreateDate = (dateString: string | null | undefined) => {
        if (!dateString) return undefined;
        const date = new Date(dateString);
        return !isNaN(date.getTime()) ? date : undefined;
      };

      const dates = {
        startDate: validateAndCreateDate(examConfig.startDate),
        endDate: validateAndCreateDate(examConfig.endDate),
        resultDate: validateAndCreateDate(examConfig.resultDate),
        giftGivingDate: validateAndCreateDate(examConfig.giftGivingDate),
      };

      setExamDates(dates);

      if (dates.startDate) setValue('startDate', dates.startDate);
      if (dates.endDate) setValue('endDate', dates.endDate);
      if (dates.resultDate) setValue('resultDate', dates.resultDate);
      if (dates.giftGivingDate)
        setValue('giftGivingDate', dates.giftGivingDate);

      if (classes && examConfig.classes && examConfig.classes.length > 0) {
        const filteredClasses = classes.filter((cls) =>
          examConfig.classes.some((ec) => ec._id === cls._id),
        );
        setSelectedClasses(filteredClasses);
        setValue('classes', filteredClasses);
      }

      if (series && examConfig.series && examConfig.series.length > 0) {
        const filteredSeries = series.filter((sr) =>
          examConfig.series.some((es) => es._id === sr._id),
        );
        setSelectedSeries(filteredSeries);
        setValue('series', filteredSeries);
      }

      if (
        disciplines &&
        examConfig.disciplinesWithLessons &&
        examConfig.disciplinesWithLessons.length > 0
      ) {
        const filteredDisciplines = disciplines.filter((disc) =>
          examConfig.disciplinesWithLessons.some(
            (ed) => ed.discipline._id === disc._id,
          ),
        );
        setSelectedDisciplines(filteredDisciplines);
        setValue('disciplinesWithLessons', examConfig.disciplinesWithLessons);

        // Synchroniser avec le store
        syncWithServerData(examConfig.disciplinesWithLessons);
      }
    }
  }, [examConfigs, classes, series, disciplines, setValue, syncWithServerData]);

  useEffect(() => {
    if (isActive) {
      setValue('isEnabled', true);
    }
  }, [isActive, setValue]);

  const onSubmit = (data: ConfigExamFormValues) => {
    const formData = { ...data };
    const isUpdate = examConfigs && examConfigs.length > 0;

    const validateDate = (date: Date | undefined) => {
      if (!date) return null;
      return date instanceof Date && !isNaN(date.getTime()) ? date : null;
    };

    // Transformer les disciplines avec leçons pour n'avoir que les IDs
    const formattedDisciplinesWithLessons = disciplinesWithLessons.map(
      (item) => ({
        discipline:
          typeof item.discipline === 'string'
            ? item.discipline
            : item.discipline._id,
        lessons: item.lessons.map((lesson) => lesson._id),
      }),
    );

    const payload: ExamConfigInput = {
      title: formData.title,
      name: formData.name,
      description: formData.description,
      pointsToEarn: formData.pointsToEarn,
      isEnabled: formData.isEnabled,
      classes: (formData.classes && formData.classes.map((cl) => cl._id)) || [],
      series: (formData.series && formData.series.map((sr) => sr._id)) || [],
      disciplinesWithLessons: formattedDisciplinesWithLessons,
      startDate: validateDate(formData.startDate) || new Date(),
      endDate: validateDate(formData.endDate) || new Date(),
      resultDate: validateDate(formData.resultDate) || new Date(),
      giftGivingDate: validateDate(formData.giftGivingDate) || new Date(),
      gifts: (formData.gifts && formData.gifts.map((gift) => gift._id)) || [],
    };

    if (isUpdate) {
      const updatePayload = {
        id: examConfigs[0]._id,
        input: payload,
      };

      updateExamConfig(updatePayload, {
        onSuccess: () => {
          showToast('Examen mis à jour avec succès', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
        onError: (error) => {
          showToast('Erreur lors de la mise à jour', 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      });
    } else {
      createExamConfig(payload, {
        onSuccess: () => {
          showToast('Examen configuré avec succès', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
        onError: (error) => {
          showToast(error.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      });
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    setValue,

    classes,
    series,
    disciplines,
    examConfigs,

    isActive,
    setIsActive,
    selectedClasses,
    selectedSeries,
    selectedDisciplines,
    examDates,

    onSubmit,
    isCreating,
    isUpdating,
  };
};
